import { Heart, Plus } from 'lucide-react'
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { useCreateFavorite, useFavorites, usePostFavorites, useToggleFavorite } from '../../api/queries'
import { isTypingTarget } from '../../lib/cardControls'
import { plural } from '../../lib/format'
import { BackLayer, isTopLayer } from '../../lib/useBack'
import { useIsDesktop } from '../../lib/useIsDesktop'
import { canAutoFocus } from '../../lib/viewport'
import { Button, buttonClass } from '../Button'
import { Dialog } from '../Dialog'
import { Popover } from '../Popover'
import { Spinner } from '../Spinner'
import { toast } from '../Toast'

/**
 * ♥ в шапке карточки. Закрашено, если пост лежит хоть в одной группе. По нажатию —
 * список групп с галочками и «Новая группа»: на компьютере всплывашка, на телефоне
 * лист снизу. Клавиша F — и на компьютере, и в Telegram Desktop, если поверх
 * карточки ничего не открыто.
 */
export function FavoriteButton({ postId }: { postId: number }) {
  const desktop = useIsDesktop()
  const layer = useContext(BackLayer)
  const membership = usePostFavorites(postId)
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement | null>(null)
  const inAny = (membership.data?.group_ids.length ?? 0) > 0
  const close = useCallback(() => setOpen(false), [])
  const openRef = useRef(open)
  useEffect(() => {
    openRef.current = open
  })

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || e.repeat) return
      // code, а не key: F работает и в русской раскладке
      if (e.code !== 'KeyF' || isTypingTarget(e.target)) return
      // Открытая всплывашка закрывается той же F; поверх карточки чужое окно — не наше.
      if (!openRef.current && !isTopLayer(layer)) return
      e.preventDefault()
      setOpen((v) => !v)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [layer])

  const label = inAny ? 'В избранном' : 'В избранное'
  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={buttonClass('ghost', 'icon')}
        title={desktop ? `${label} (F)` : label}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Heart size={18} fill={inAny ? 'currentColor' : 'none'} />
      </button>
      {open &&
        (desktop ? (
          <FavoritePopover anchor={anchor} onClose={close}>
            <GroupPicker postId={postId} compact />
          </FavoritePopover>
        ) : (
          <Dialog title="В избранное" onClose={close}>
            <GroupPicker postId={postId} />
          </Dialog>
        ))}
    </>
  )
}

// ── всплывашка у кнопки (компьютер) ──────────────────────────────────────────

function FavoritePopover({
  anchor,
  onClose,
  children,
}: {
  anchor: RefObject<HTMLButtonElement | null>
  onClose: () => void
  children: ReactNode
}) {
  return (
    <Popover anchor={anchor} onClose={onClose} label="В избранное">
      <div className="flex items-center justify-between border-b border-line px-3 py-2">
        <span className="text-[13px] font-semibold">В избранное</span>
        <kbd className="rounded bg-surface px-1.5 py-0.5 font-sans text-[11px] text-muted ring-1 ring-line ring-inset">F</kbd>
      </div>
      {children}
    </Popover>
  )
}


function GroupPicker({ postId, compact = false }: { postId: number; compact?: boolean }) {
  const groups = useFavorites()
  const membership = usePostFavorites(postId)
  const toggle = useToggleFavorite()
  const create = useCreateFavorite()
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const ids = new Set(membership.data?.group_ids ?? [])
  // Порядок по созданию, а не по последней правке: галочки не прыгают под курсором.
  const list = [...(groups.data ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id - a.id)
  const showForm = creating || (groups.isSuccess && list.length === 0)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const value = name.trim()
    if (!value || create.isPending) return
    create.mutate(value, {
      onSuccess: (group) => {
        toggle.mutate({ groupId: group.id, postId, inGroup: true })
        toast(`Добавлено в «${group.name}»`)
        setName('')
        setCreating(false)
      },
    })
  }

  const row = compact ? 'min-h-9 px-3' : 'min-h-12 px-1'
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className={`min-h-0 flex-1 overflow-y-auto ${compact ? 'py-1' : '-mx-1'}`}>
        {groups.isPending ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : groups.isError ? (
          <div className="px-3 py-4 text-sm text-muted">
            Не удалось загрузить группы.{' '}
            <button type="button" className="font-medium text-link hover:underline" onClick={() => void groups.refetch()}>
              Повторить
            </button>
          </div>
        ) : list.length === 0 ? (
          <p className={`py-3 text-sm text-pretty text-muted ${compact ? 'px-3' : 'px-1'}`}>
            Групп пока нет. Назовите первую — пост сразу в неё попадёт.
          </p>
        ) : (
          <ul>
            {list.map((g) => (
              <li key={g.id}>
                <label className={`flex cursor-pointer items-center gap-3 rounded-lg hover:bg-surface ${row}`}>
                  <input
                    type="checkbox"
                    checked={ids.has(g.id)}
                    disabled={membership.isPending}
                    onChange={(e) => toggle.mutate({ groupId: g.id, postId, inGroup: e.target.checked })}
                    className="size-4 shrink-0 accent-[var(--accent)]"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm">{g.name}</span>
                  <span className="shrink-0 text-xs text-muted tabular-nums">
                    {g.count} {plural(g.count, 'пост', 'поста', 'постов')}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
      {toggle.isError && <p className="px-3 pb-1 text-sm text-danger">Не получилось сохранить.</p>}
      <div className={`border-t border-line ${compact ? 'p-2' : '-mx-4 mt-2 px-4 pt-3 pb-[max(0.25rem,var(--safe-bottom))]'}`}>
        {showForm ? (
          <form onSubmit={submit} className="flex items-center gap-2">
            <input
              autoFocus={(creating || !compact) && canAutoFocus()}
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Новая группа, например «На неделю»"
              aria-label="Название новой группы"
              className="h-11 min-w-0 flex-1 rounded-lg bg-surface px-3 text-[15px] ring-1 ring-line outline-none ring-inset placeholder:text-muted focus:ring-fg/40 desk:h-8 desk:text-[13px]"
            />
            <Button type="submit" variant="primary" disabled={!name.trim() || create.isPending}>
              {create.isPending && <Spinner size={14} className="text-accent-fg" />}
              Создать
            </Button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className={`flex w-full items-center gap-2 rounded-lg text-sm font-medium hover:bg-surface ${
              compact ? 'h-8 px-2' : 'h-11 px-1'
            }`}
          >
            <Plus size={16} />
            Новая группа
          </button>
        )}
        {create.isError && <p className="mt-1.5 text-sm text-danger">Не получилось создать группу.</p>}
      </div>
    </div>
  )
}
